import React, { useState, useEffect } from 'react';
import { ArrowLeft, FileText } from 'lucide-react';

const TermsAndConditions = ({ onBack }) => {
  const [scrollProgress, setScrollProgress] = useState(0);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;
    setScrollProgress(progress);
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 p-4">
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #a5b4fc;
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #818cf8;
        }
        .scroll-shadow {
          background: linear-gradient(white 30%, rgba(255,255,255,0)),
                      linear-gradient(rgba(255,255,255,0), white 70%) 0 100%,
                      radial-gradient(farthest-side at 50% 0, rgba(0,0,0,.2), rgba(0,0,0,0)),
                      radial-gradient(farthest-side at 50% 100%, rgba(0,0,0,.2), rgba(0,0,0,0)) 0 100%;
          background-repeat: no-repeat;
          background-color: white;
          background-size: 100% 40px, 100% 40px, 100% 14px, 100% 14px;
          background-attachment: local, local, scroll, scroll;
        }
      `}</style>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-2xl mb-6 p-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={onBack}
              className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Terms and Conditions</h1>
                <p className="text-gray-600">123zzz Application</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content - Professional Scrollable Container */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Fixed Header in Content */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Terms and Conditions</h2>
                <p className="text-sm text-gray-600">123zzz Application - Complete Legal Document</p>
              </div>
              <div className="text-xs text-gray-500 bg-white px-3 py-1 rounded-full">
                {scrollProgress < 95 ? 'Scroll to read all sections' : 'Document completed ✓'}
              </div>
            </div>
            {/* Progress Bar */}
            <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-indigo-500 to-purple-600 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${scrollProgress}%` }}
              ></div>
            </div>
          </div>
          
          {/* Scrollable Content Area */}
          <div 
            className="max-h-[70vh] overflow-y-auto custom-scrollbar scroll-shadow"
            onScroll={handleScroll}
          >
            <div className="p-8">
              <div className="prose prose-gray max-w-none">
                <div className="text-sm text-gray-500 mb-6 sticky top-0 bg-white/90 backdrop-blur-sm p-2 rounded-lg border border-gray-200 flex items-center justify-between">
                  <span>Last updated: January 2025</span>
                  <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                    {Math.round(scrollProgress)}% read
                  </span>
                </div>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">1. ACCEPTANCE OF TERMS</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms and Conditions govern the use of the 123zzz mobile application and all related services provided by our company based in Brisbane, Queensland, Australia. By downloading, installing, or using our application, you agree to be bound by these terms and applicable laws including the Australian Consumer Law, GDPR, COPPA, and international privacy regulations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">2. SERVICE DESCRIPTION</h2>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-4">
                <p className="text-gray-700 leading-relaxed mb-4">
                  123zzz is a premium mobile application that uses advanced artificial intelligence technology to generate personalized bedtime stories for children aged 2-12. Our service creates unique, engaging narratives tailored to your child's interests, age, and preferences.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border-2 border-indigo-200 rounded-xl p-6">
                  <h3 className="font-bold text-indigo-800 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                    Core Features
                  </h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                    <li>AI-powered story generation with custom characters and themes</li>
                    <li>High-quality AI-generated illustrations for each story</li>
                    <li>4-part narrative structure (situation, conflict, adventure, resolution)</li>
                    <li>Multi-language support (English, French)</li>
                    <li>Story personalization based on child's interests and moral lessons</li>
                    <li>Cloud storage and synchronization across devices</li>
                  </ul>
                </div>
                <div className="bg-white border-2 border-purple-200 rounded-xl p-6">
                  <h3 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                    Technical Specifications
                  </h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                    <li>Advanced AI algorithms for content generation</li>
                    <li>Professional-grade image generation technology</li>
                    <li>Secure Firebase authentication and data storage</li>
                    <li>Real-time story creation and delivery</li>
                    <li>Automatic content moderation for child safety</li>
                    <li>Cross-platform compatibility (iOS, Android, Web)</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">3. SUBSCRIPTION PLANS AND PRICING</h2>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 mb-6">
                <p className="text-amber-800 font-semibold mb-2">🎯 Simple, Transparent Pricing</p>
                <p className="text-amber-700 text-sm">
                  Choose the plan that works best for your family. All plans include high-quality AI-generated stories and illustrations.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-gray-50 to-slate-50 border-2 border-gray-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-gray-800 text-lg">Free Plan</h3>
                    <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-semibold">
                      Trial
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 text-xs">✓</span>
                      </span>
                      <span className="text-gray-700">3 complimentary stories (lifetime)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 text-xs">✓</span>
                      </span>
                      <span className="text-gray-700">AI-generated illustrations</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 text-xs">✓</span>
                      </span>
                      <span className="text-gray-700">Multi-language support</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 text-xs">✓</span>
                      </span>
                      <span className="text-gray-700">Cloud storage and synchronization</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-blue-800 text-lg">On-Demand Plan</h3>
                    <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                      $0.49 per story
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-xs">✓</span>
                      </span>
                      <span className="text-gray-700">Pay only for what you use</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-xs">✓</span>
                      </span>
                      <span className="text-gray-700">Perfect for occasional use</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-xs">✓</span>
                      </span>
                      <span className="text-gray-700">All core features included</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-xs">✓</span>
                      </span>
                      <span className="text-gray-700">No subscription commitment</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 bg-gradient-to-l from-purple-500 to-pink-500 text-white px-4 py-1 rounded-bl-xl text-xs font-bold">
                    MOST POPULAR
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-purple-800 text-lg">Standard Plan</h3>
                    <div className="text-right">
                      <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-semibold block">
                        $9.99/month
                      </span>
                      <span className="text-xs text-purple-600 mt-1 block">≈ $0.20 per story</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-600 text-xs">✓</span>
                      </span>
                      <span className="text-gray-700"><strong>Up to 50 stories per month</strong></span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-600 text-xs">✓</span>
                      </span>
                      <span className="text-gray-700">Best value for regular users</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-600 text-xs">✓</span>
                      </span>
                      <span className="text-gray-700">All premium features included</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-600 text-xs">✓</span>
                      </span>
                      <span className="text-gray-700">Cancel anytime</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">4. ELIGIBILITY AND AGE RESTRICTIONS</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Users must be 18+ years old to create accounts and make purchases</li>
                <li>Children under 13 may only use this application under direct parental supervision with verified parental consent</li>
                <li>For US users: Full COPPA compliance with enhanced parental controls</li>
                <li>EU users: GDPR Article 8 compliance for children's data processing</li>
                <li>Account creation requires valid email verification</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">5. BILLING AND PAYMENT TERMS</h2>
              <div className="bg-green-50 border-l-4 border-green-400 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-sm">💳</span>
                  </span>
                  <h3 className="font-semibold text-green-800">Secure Payment Processing</h3>
                </div>
                <p className="text-green-700 text-sm">
                  All payments are processed securely through Stripe, meeting the highest industry standards for payment security and data protection.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white border border-blue-200 rounded-xl p-6">
                    <h3 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
                      <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                      On-Demand Payments
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm ml-4">
                      <li>Immediate charge of $0.49 per story</li>
                      <li>Payment processed upon story generation</li>
                      <li>No recurring charges or subscriptions</li>
                      <li>Refunds available within 24 hours if story fails to generate</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border border-purple-200 rounded-xl p-6">
                    <h3 className="font-bold text-purple-800 mb-4 flex items-center gap-2">
                      <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
                      Standard Subscription
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm ml-4">
                      <li>Monthly charge of $9.99 billed on signup date</li>
                      <li>Automatic renewal 24 hours before period ends</li>
                      <li>Cancel anytime through account settings</li>
                      <li>Pro-rated refunds for unused subscription time</li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-amber-50 rounded-xl p-6">
                  <h3 className="font-bold text-amber-800 mb-4">Important Billing Information</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <ul className="list-disc list-inside text-amber-700 space-y-2 text-sm ml-4">
                        <li>Price changes require 30 days advance notice to existing subscribers</li>
                        <li>Failed payments may result in service suspension after 48-hour grace period</li>
                        <li>Subscription limits reset monthly on your billing anniversary</li>
                      </ul>
                    </div>
                    <div>
                      <ul className="list-disc list-inside text-amber-700 space-y-2 text-sm ml-4">
                        <li>All prices are in USD and include applicable taxes</li>
                        <li>Payment methods: Credit/debit cards via Stripe</li>
                        <li>Billing disputes must be reported within 60 days</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">6. AI-GENERATED CONTENT AND INTELLECTUAL PROPERTY</h2>
              
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                    <span className="text-indigo-600 text-lg">🤖</span>
                  </span>
                  <h3 className="font-bold text-indigo-800">AI Technology & Content Ownership</h3>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Our stories are generated using proprietary AI algorithms combined with advanced machine learning technologies. 
                  Understanding content ownership and usage rights is important for all users.
                </p>
              </div>

              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <span className="w-4 h-4 bg-gray-400 rounded-full"></span>
                      123zzz Owns
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm ml-4">
                      <li>AI technology and algorithms</li>
                      <li>Software application and infrastructure</li>
                      <li>Story generation templates and frameworks</li>
                      <li>Image generation systems and processes</li>
                      <li>Brand, logos, and application design</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border-2 border-green-200 rounded-xl p-6">
                    <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2">
                      <span className="w-4 h-4 bg-green-500 rounded-full"></span>
                      You Own
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm ml-4">
                      <li>Personal use rights to your generated stories</li>
                      <li>Your child's personal information and preferences</li>
                      <li>Family data inputs and customizations</li>
                      <li>Generated content for personal, non-commercial use</li>
                      <li>The right to download and share your stories privately</li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                  <h3 className="font-bold text-red-800 mb-4 flex items-center gap-2">
                    <span className="w-4 h-4 bg-red-500 rounded-full"></span>
                    Usage Restrictions
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-red-700 font-semibold mb-2 text-sm">Prohibited Uses:</p>
                      <ul className="list-disc list-inside text-red-700 space-y-1 text-sm ml-4">
                        <li>Commercial resale or distribution</li>
                        <li>Reverse engineering our AI technology</li>
                        <li>Creating derivative commercial products</li>
                        <li>Mass distribution or public broadcasting</li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-red-700 font-semibold mb-2 text-sm">Allowed Uses:</p>
                      <ul className="list-disc list-inside text-red-700 space-y-1 text-sm ml-4">
                        <li>Personal family storytelling</li>
                        <li>Private sharing with friends/family</li>
                        <li>Educational use within families</li>
                        <li>Personal archiving and collection</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="font-bold text-blue-800 mb-3">Content Moderation & Safety</h3>
                  <p className="text-blue-700 text-sm mb-3">
                    We employ advanced content moderation systems to ensure all generated stories are appropriate for children:
                  </p>
                  <ul className="list-disc list-inside text-blue-700 space-y-1 text-sm ml-4">
                    <li>Automatic filtering of inappropriate content</li>
                    <li>Age-appropriate language and themes</li>
                    <li>Positive moral lessons and values</li>
                    <li>Regular review and improvement of safety measures</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">7. DATA PROTECTION AND PRIVACY</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Compliance:</h3>
                  <p className="text-gray-700 ml-4">Full adherence to GDPR, COPPA, Australian Privacy Principles, and CCPA</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Security Measures:</h3>
                  <p className="text-gray-700 ml-4">AES-256 encryption, secure cloud storage, regular security audits</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">User Rights:</h3>
                  <p className="text-gray-700 ml-4">Access, correction, deletion, and portability of personal data upon request</p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">8. LIMITATION OF LIABILITY</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Service provided "as is" subject to statutory guarantees under Australian Consumer Law</li>
                <li>Maximum liability limited to 12 months of subscription fees paid</li>
                <li>Not liable for indirect, consequential, or punitive damages</li>
                <li>Users responsible for reviewing AI-generated content appropriateness</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">9. GOVERNING LAW</h2>
              <p className="text-gray-700 leading-relaxed">
                These terms are governed by Queensland, Australia law. International users retain local consumer protection rights. Disputes resolved through Australian courts or binding arbitration.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">10. CONTACT INFORMATION</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Customer Support:</h3>
                  <p className="text-gray-700 ml-4">Available 24/7 through in-app chat</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Legal Inquiries:</h3>
                  <p className="text-gray-700 ml-4">contact.123zzz@gmail.com</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Location:</h3>
                  <p className="text-gray-700 ml-4">Brisbane, Queensland, Australia</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Response Time:</h3>
                  <p className="text-gray-700 ml-4">Maximum 48 hours for all inquiries</p>
                </div>
              </div>
            </section>

            <div className="mt-12 p-6 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600 text-center">
                These terms are effective immediately upon acceptance and supersede all previous versions. 
                Regular updates ensure continued legal compliance and service improvement.
              </p>
            </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;